/**
 * AttachmentsStrip — compact, inline horizontal row of linked DMS docs.
 * Intended for narrow contexts like a deliverable row under a rock.
 *
 * For richer contexts (detail panel, form section), use <AttachmentsList />.
 */

import { useMemo } from 'react';
import { ExternalLink, FileText, Link2, X } from 'lucide-react';
import { useDocumentDetail } from '@/features/documents/hooks/useDocuments';
import { DmsDocumentPicker } from './DmsDocumentPicker';
import { useAttachments, useCreateAttachment, useDeleteAttachment } from '../hooks/useAttachments';
import type { AttachmentEntityType } from '../types';

interface AttachmentsStripProps {
  entityType: AttachmentEntityType;
  entityId: string;
  readOnly?: boolean;
}

function DocumentChip({
  documentId,
  onDelete,
}: {
  documentId: string;
  onDelete?: () => void;
}) {
  const { data: detail } = useDocumentDetail(documentId);
  const name = detail?.name ?? documentId;
  const dmsUrl = `/documents?doc=${documentId}`;

  return (
    <div className="flex items-center gap-1.5 group/doc">
      <FileText className="h-3 w-3 text-muted-foreground shrink-0" />
      <a
        href={dmsUrl}
        className="text-xs text-blue-600 hover:underline truncate"
        onClick={(e) => e.stopPropagation()}
      >
        {name}
      </a>
      <a
        href={dmsUrl}
        className="opacity-0 group-hover/doc:opacity-100 transition-opacity text-muted-foreground hover:text-foreground shrink-0"
        title="Open in DMS"
        onClick={(e) => e.stopPropagation()}
      >
        <ExternalLink className="h-3 w-3" />
      </a>
      {onDelete && (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onDelete(); }}
          className="opacity-0 group-hover/doc:opacity-100 transition-opacity text-muted-foreground hover:text-destructive shrink-0"
          aria-label="Remove link"
        >
          <X className="h-3 w-3" />
        </button>
      )}
    </div>
  );
}

export function AttachmentsStrip({ entityType, entityId, readOnly = false }: AttachmentsStripProps) {
  const { data: attachments = [] } = useAttachments(entityType, entityId);
  const createAttachment = useCreateAttachment();
  const deleteAttachment = useDeleteAttachment();

  const excludeIds = useMemo(
    () => new Set(attachments.map((a) => a.documentId)),
    [attachments],
  );

  if (readOnly && attachments.length === 0) return null;

  return (
    <div className="pl-6 flex flex-wrap items-center gap-x-3 gap-y-1 mt-0.5">
      {attachments.map((att) => (
        <DocumentChip
          key={att.id}
          documentId={att.documentId}
          onDelete={
            readOnly
              ? undefined
              : () => deleteAttachment.mutate({ id: att.id, entityType, entityId })
          }
        />
      ))}
      {!readOnly && (
        <DmsDocumentPicker
          size="sm"
          excludeIds={excludeIds}
          onSelect={(doc) =>
            createAttachment.mutate({
              entity_type: entityType,
              entity_id: entityId,
              document_id: doc.id,
            })
          }
          trigger={
            <button
              type="button"
              className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors"
              title="Link DMS document"
            >
              <Link2 className="h-3 w-3" />
              <span>Link</span>
            </button>
          }
        />
      )}
    </div>
  );
}
